/**
 * Messages Hooks
 * TanStack Query hooks for message operations
 * Uses infinite query format for pagination
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import * as messagesService from '@/services/messages.service';
import type { PaginatedMessagesResponse } from '@/services/messages.service';
import type { Message } from '@/types/api.types';

/**
 * Helper type for infinite query data
 */
type InfiniteMessagesData = InfiniteData<PaginatedMessagesResponse>;

/**
 * Infinite Query hook for bi-directional message pagination
 * Loads latest messages first, then older messages on scroll up
 * Optimized for smooth infinite scroll experience
 */
export const useMessagesInfinite = (
  channelId?: string | null,
  dmThreadId?: string | null,
  options: { pageSize?: number } = {}
) => {
  const { pageSize = 50 } = options;
  const conversationKey = channelId || dmThreadId;

  type PageParam = { before?: string; after?: string } | undefined;

  return useInfiniteQuery<PaginatedMessagesResponse, Error, InfiniteData<PaginatedMessagesResponse>, (string | null | undefined)[], PageParam>({
    queryKey: ['messages', 'infinite', conversationKey],
    queryFn: async ({ pageParam }) => {
      return messagesService.listMessages({
        channelId: channelId || undefined,
        dmThreadId: dmThreadId || undefined,
        limit: pageSize,
        // pageParam contains the cursor for loading older messages
        before: pageParam?.before,
        after: pageParam?.after,
      });
    },
    initialPageParam: undefined,
    // For loading OLDER messages (scroll up) - returns cursor for previous page
    getNextPageParam: (lastPage): PageParam => {
      if (!lastPage.hasOlderMessages || !lastPage.oldestMessageId) {
        return undefined;
      }
      return { before: lastPage.oldestMessageId };
    },
    // For loading NEWER messages (new messages arrive via WebSocket, but this is for initial load edge cases)
    getPreviousPageParam: (firstPage): PageParam => {
      if (!firstPage.hasNewerMessages || !firstPage.newestMessageId) {
        return undefined;
      }
      return { after: firstPage.newestMessageId };
    },
    enabled: !!(channelId || dmThreadId),
    staleTime: 30 * 1000, // 30 seconds
    // Keep 10 pages in memory for smooth scrolling
    maxPages: 10,
  });
};

/**
 * Helper to flatten infinite query pages into a single messages array
 * Messages are returned in chronological order (oldest first)
 */
export const flattenInfiniteMessages = (
  data: ReturnType<typeof useMessagesInfinite>['data']
): Message[] => {
  if (!data?.pages) return [];

  // Pages are in reverse chronological order (newest first, then older pages)
  // Each page's messages are in chronological order (oldest first within the page)
  // We need to reverse the pages array to get chronological order
  const allMessages: Message[] = [];

  // Iterate pages in reverse (oldest pages first)
  for (let i = data.pages.length - 1; i >= 0; i--) {
    allMessages.push(...data.pages[i].messages);
  }

  return allMessages;
};

/**
 * Helper to check if there are more older messages to load
 */
export const hasMoreOlderMessages = (
  data: ReturnType<typeof useMessagesInfinite>['data']
): boolean => {
  if (!data?.pages?.length) return false;
  // The last page contains the oldest messages
  const oldestPage = data.pages[data.pages.length - 1];
  return oldestPage.hasOlderMessages;
};

/**
 * Query hook to get a single message
 */
export const useMessage = (messageId: string | null) => {
  return useQuery({
    queryKey: ['messages', 'single', messageId],
    queryFn: () => messagesService.getMessage(messageId!),
    enabled: !!messageId,
  });
};

/**
 * Mutation hook to send a message
 * Uses HTTP response to update cache as a reliable fallback.
 * WebSocket may also deliver the message — deduplication prevents doubles.
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      content: string;
      channelId?: string;
      dmThreadId?: string;
      replyToMessageId?: string;
      isVoiceMessage?: boolean;
    }) => messagesService.createMessage(data),
    onSuccess: (message, variables) => {
      if (!message?.id) return;

      const conversationKey = variables.channelId || variables.dmThreadId;
      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages?.length) {
          return {
            pages: [{
              messages: [message],
              hasOlderMessages: false,
              hasNewerMessages: false,
              oldestMessageId: message.id,
              newestMessageId: message.id,
            }],
            pageParams: [undefined],
          };
        }

        // Deduplicate: WebSocket may have already added this message
        for (const page of old.pages) {
          if (page.messages.some((msg) => msg.id === message.id)) {
            return old;
          }
        }

        const newPages = [...old.pages];
        newPages[0] = {
          ...newPages[0],
          messages: [...newPages[0].messages, message],
          newestMessageId: message.id,
        };
        return { ...old, pages: newPages };
      });
    },
  });
};

/**
 * Helper to find conversation key from message ID in infinite query cache.
 * Exported for reuse by other message-related mutations (e.g. reactions).
 */
export const findConversationInInfiniteCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  messageId: string
): { conversationKey: string; previousData: InfiniteMessagesData } | null => {
  const allQueries = queryClient.getQueriesData<InfiniteMessagesData>({
    queryKey: ['messages', 'infinite'],
  });

  for (const [queryKey, data] of allQueries) {
    if (!data?.pages) continue;

    for (const page of data.pages) {
      if (page.messages.some((msg) => msg.id === messageId)) {
        return {
          conversationKey: queryKey[2] as string,
          previousData: data,
        };
      }
    }
  }

  return null;
};

/**
 * Mutation hook to update (edit) a message
 */
export const useUpdateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
      messagesService.updateMessage(messageId, content),
    onMutate: async ({ messageId, content }) => {
      const found = findConversationInInfiniteCache(queryClient, messageId);
      if (!found) return { previousData: undefined };

      const queryKey = ['messages', 'infinite', found.conversationKey];

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Optimistically update
      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === messageId
                ? { ...msg, content, is_edited: true, edited_at: new Date().toISOString() }
                : msg
            ),
          })),
        };
      });

      return { previousData: found.previousData, conversationKey: found.conversationKey };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.conversationKey) {
        queryClient.setQueryData(
          ['messages', 'infinite', context.conversationKey],
          context.previousData
        );
      }
    },
    onSuccess: (updatedMessage, _variables, context) => {
      // Update with real message from server
      if (context?.conversationKey) {
        const queryKey = ['messages', 'infinite', context.conversationKey];
        queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              ),
            })),
          };
        });
      }

      // Update single message cache if it exists
      queryClient.setQueryData(['messages', 'single', updatedMessage.id], updatedMessage);
    },
  });
};

/**
 * Mutation hook to delete a message
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => messagesService.deleteMessage(messageId),
    onMutate: async (messageId) => {
      const found = findConversationInInfiniteCache(queryClient, messageId);
      if (!found) return { previousData: undefined };

      const queryKey = ['messages', 'infinite', found.conversationKey];

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Optimistically remove message
      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((msg) => msg.id !== messageId),
          })),
        };
      });

      return { previousData: found.previousData, conversationKey: found.conversationKey, messageId };
    },
    onError: (_err, _messageId, context) => {
      // Rollback on error
      if (context?.previousData && context?.conversationKey) {
        queryClient.setQueryData(
          ['messages', 'infinite', context.conversationKey],
          context.previousData
        );
      }
    },
    onSuccess: (_, messageId) => {
      // Remove single message cache
      queryClient.removeQueries({ queryKey: ['messages', 'single', messageId] });
    },
    onSettled: (_data, _error, _messageId, context) => {
      // Refetch to ensure cache is consistent after optimistic update
      if (context?.conversationKey) {
        queryClient.invalidateQueries({
          queryKey: ['messages', 'infinite', context.conversationKey],
        });
      }
    },
  });
};

/**
 * Mutation hook to hide/show a link preview
 * Only the message author can hide their link previews
 */
export const useHideLinkPreview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      linkId,
      hidden,
    }: {
      messageId: string;
      linkId: string;
      hidden: boolean;
    }) => messagesService.updateLinkVisibility(messageId, linkId, hidden),
    onMutate: async ({ messageId, linkId, hidden }) => {
      const found = findConversationInInfiniteCache(queryClient, messageId);
      if (!found) return { previousData: undefined };

      const queryKey = ['messages', 'infinite', found.conversationKey];

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Optimistically update the link's hidden status
      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              if (msg.id !== messageId) return msg;
              return {
                ...msg,
                message_links: msg.message_links?.map((link) =>
                  link.id === linkId ? { ...link, hidden } : link
                ),
              };
            }),
          })),
        };
      });

      return { previousData: found.previousData, conversationKey: found.conversationKey };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.conversationKey) {
        queryClient.setQueryData(
          ['messages', 'infinite', context.conversationKey],
          context.previousData
        );
      }
    },
  });
};

/**
 * Forward a message to a different channel or DM thread
 * WebSocket `message:new` handles adding it to the destination cache
 */
export const useForwardMessage = () => {
  return useMutation({
    mutationFn: ({
      messageId,
      destinationChannelId,
      destinationDmThreadId,
      comment,
    }: {
      messageId: string;
      destinationChannelId?: string;
      destinationDmThreadId?: string;
      comment?: string;
    }) =>
      messagesService.forwardMessage(messageId, {
        destinationChannelId,
        destinationDmThreadId,
        comment,
      }),
  });
};
